// `extension-criteria-external-source-filters-data-helpers.ng1.ts` — the five per-type handlers
// for the external-source filters, the criteria builder's ninth and last section.
//
// ⚠️ IT IS NOT THE ATS-FILTERS MODEL WITH DIFFERENT CLASS NAMES, though the two tables are
// drawn identically. Three things move:
//
//   1. `conditionType` lives on the FILTER, beside `value` — not inside it. The ATS version
//      puts `condition` in the data object, so the same field is one level deeper there.
//   2. THE CONDITION OPTIONS ARE PER FIELD. Each field's configuration carries
//      `supportedConditionTypes`, and the select offers exactly those — where the ATS section
//      has two fixed lists for the whole tenant. A field may support one condition, or all
//      three, and the display text is looked up from a map rather than carried with the option.
//   3. THE DEFAULT CONDITION IS `supportedConditionTypes[0]`, the first the field allows.
//      Numeric and date filters get NO `conditionType` at all — their `getNewData` omits the
//      key rather than setting it to null, so a numeric filter that carries one is malformed.
//
// And the `INCLUDE_ALL` display text differs from the ATS section's: here it reads "Include"
// in BOTH maps, where the ATS section says "Include all" for multi-value fields.
import {
  type ExternalSourceCriteriaFilterConditionType,
  type ExternalSourceCriteriaFilterFieldType,
} from '@woo-ext/api'

/** `IExternalSourceExtensionCriteriaFilterSupportedValue`. */
export interface ExternalSourceSupportedValue {
  value: string
  displayName: string
}

/** `ICriteriaFilterConfiguration` — one field the source can be filtered on. */
export interface ExternalSourceFilterField {
  displayName: string
  fieldName: string
  fieldType: ExternalSourceCriteriaFilterFieldType
  supportedConditionTypes: ExternalSourceCriteriaFilterConditionType[]
  supportedValues: ExternalSourceSupportedValue[]
}

export interface ExternalSourceTextValue {
  type: 'TEXT'
  values: string[]
}
export interface ExternalSourceNumericValue {
  type: 'NUMERIC'
  min: number | null
  max: number | null
}
export interface ExternalSourceDateValue {
  type: 'DATE'
  from: Date | null
  to: Date | null
}
export interface ExternalSourceMultiselectValue {
  type: 'MULTI_SELECT'
  values: string[]
}
export interface ExternalSourceSingleselectValue {
  type: 'SINGLE_SELECT'
  value: string | null
}

export type ExternalSourceFilterValue =
  | ExternalSourceTextValue
  | ExternalSourceNumericValue
  | ExternalSourceDateValue
  | ExternalSourceMultiselectValue
  | ExternalSourceSingleselectValue

/** `IExtensionCriteriaExternalSourceFilter` — one row. */
export interface ExternalSourceFilterRow {
  fieldName?: string
  /** ⚠️ ON THE ROW, not inside `value`. Absent for numeric and date. */
  conditionType?: ExternalSourceCriteriaFilterConditionType
  value?: ExternalSourceFilterValue
}

/**
 * `getNewData` — what a row becomes when its Field select changes.
 *
 * Takes the field's whole configuration, not just its type, because the default condition comes
 * from `supportedConditionTypes[0]`.
 */
export function newExternalSourceFilterRow(
  field: ExternalSourceFilterField,
  now: Date = new Date(),
): ExternalSourceFilterRow {
  const base = { fieldName: field.fieldName }
  const firstCondition = field.supportedConditionTypes[0]

  switch (field.fieldType) {
    case 'TEXT':
      return { ...base, conditionType: firstCondition, value: { type: 'TEXT', values: [] } }
    case 'MULTI_SELECT':
      return { ...base, conditionType: firstCondition, value: { type: 'MULTI_SELECT', values: [] } }
    case 'SINGLE_SELECT':
      return { ...base, conditionType: firstCondition, value: { type: 'SINGLE_SELECT', value: null } }
    // ⚠️ No `conditionType` key at all — see the note at the top.
    case 'NUMERIC':
      return { ...base, value: { type: 'NUMERIC', min: null, max: null } }
    case 'DATE':
      return { ...base, value: { type: 'DATE', from: null, to: now } }
  }
}

/** `isValid` — the condition is part of it for the three that have one, and only those. */
export function isExternalSourceFilterValid(row: ExternalSourceFilterRow): boolean {
  const value = row.value
  if (value === undefined) return false
  switch (value.type) {
    case 'TEXT':
    case 'MULTI_SELECT':
      return row.conditionType !== undefined && value.values.length > 0
    case 'SINGLE_SELECT':
      return row.conditionType !== undefined && value.value !== null
    case 'NUMERIC':
      return value.min !== null && value.max !== null
    case 'DATE':
      return value.to !== null && (value.from === null || value.to > value.from)
  }
}

/** `externalSourceFiltersTableHeadlinesByType` — the same split as the ATS table. */
export const EXTERNAL_SOURCE_FILTER_HEADLINES: Record<ExternalSourceCriteriaFilterFieldType, readonly string[]> = {
  TEXT: ['Condition'],
  MULTI_SELECT: ['Condition'],
  SINGLE_SELECT: ['Condition'],
  NUMERIC: ['From', 'To'],
  DATE: ['From', 'To'],
}

/**
 * `externalSourceFiltersConditionDisplayTextMap`.
 *
 * ⚠️ `INCLUDE_ALL` READS "Include" HERE, where the ATS section's multi-value list says
 * "Include all". The single-select map is identical to this one, unlike the ATS section's,
 * where the two lists genuinely differ.
 */
export const EXTERNAL_SOURCE_CONDITION_TEXT: Record<ExternalSourceCriteriaFilterConditionType, string> = {
  INCLUDE_ANY: 'Include any',
  INCLUDE_ALL: 'Include',
  EXCLUDE: 'Exclude',
}

/** The field a row names, from the source's configuration. */
export function externalSourceFieldFor(
  fields: readonly ExternalSourceFilterField[],
  fieldName: string | undefined,
): ExternalSourceFilterField | undefined {
  if (fieldName === undefined || fieldName === '') return undefined
  return fields.find((field) => field.fieldName === fieldName)
}

/**
 * `onNgModelChange` (external-source-filters.component.ng1.ts:130) — the rows each configured
 * source starts with.
 *
 * ⚠️ EVERY CONFIGURED SOURCE GETS ONE EMPTY ROW, and the saved criteria are merged OVER that
 * seed rather than replacing it. So a source with nothing saved still shows a Field select
 * waiting to be filled — which is what the screen looks like at rest, and what an empty map
 * does NOT produce. The same rule holds for the ATS table, whose `initAtsFilters(null)` returns
 * `[{}]`.
 */
export function seedExternalSourceFilters(
  configuredHosts: readonly string[],
  saved: Readonly<Record<string, readonly ExternalSourceFilterRow[]>> = {},
): Record<string, ExternalSourceFilterRow[]> {
  const seeded: Record<string, ExternalSourceFilterRow[]> = {}
  for (const host of configuredHosts) seeded[host] = [{}]
  for (const [host, rows] of Object.entries(saved)) seeded[host] = [...rows]
  return seeded
}
